import {
  ActionableNotification,
  DataTableSkeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import plur from 'plur';
import { format0 } from 'utils/number';
import { DrillInHeader } from '../drill-in-header';
import { useAffectedSlots } from '../hooks';
import { EMPTY } from '../utils';
import type { AffectedSlotRow } from '../types';
import styles from './affected-slots-drill-in.module.scss';

/** Which half of the affected-slots list a drill-in shows. */
export type AffectedSlotsFilter = 'unscheduled' | 'unvalued';

const COPY: Record<
  AffectedSlotsFilter,
  { title: string; noun: (n: number) => string; empty: string }
> = {
  unscheduled: {
    title: 'Unscheduled properties',
    noun: (n) => `${plur('property', n)} unscheduled`,
    empty: 'No unscheduled properties.',
  },
  unvalued: {
    title: 'Properties that will not be valued',
    noun: (n) => `${plur('property', n)} will not be valued`,
    empty: 'No properties will go unvalued.',
  },
};

/**
 * The read-only affected-slots table — one row per property, the reason rendered
 * verbatim (the server labels each with its one reason).
 *
 * @param {object} props - The rows to render.
 * @param {AffectedSlotRow[]} props.rows - The filtered affected-slot rows.
 * @return {JSX.Element} The table.
 */
function AffectedSlotsTable({ rows }: { rows: AffectedSlotRow[] }) {
  return (
    <div className={styles.tableWrapper}>
      <Table size="sm" aria-label="Affected properties">
        <TableHead>
          <TableRow>
            <TableHeader>Property</TableHeader>
            <TableHeader>API</TableHeader>
            <TableHeader>Category</TableHeader>
            <TableHeader>Reason</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.slotId}>
              <TableCell>{row.wellName || row.slotId}</TableCell>
              <TableCell>{row.apiNumber ?? EMPTY}</TableCell>
              <TableCell>{row.category ?? EMPTY}</TableCell>
              <TableCell>{row.reason}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export interface AffectedSlotsDrillInProps {
  /** The calculation whose affected slots to load. */
  calculationId: number;
  /** Which half of the list to show — the readout line the "View" sat on. */
  filter: AffectedSlotsFilter;
  /** Collapse the drill-in and return to the coverage readout. */
  onClose: () => void;
}

/**
 * The in-page coverage drill-in (07-COV §2.1, INS-12496): the named list behind a
 * coverage readout's "View" affordance, opened inline under the readout instead
 * of the old full-page navigation that 406'd (the action serves only a jbuilder).
 * Fetches once via {@link useAffectedSlots} and shows the half of the list the
 * clicked readout named — unscheduled rows, or unvalued rows — each with its one
 * reason. Loading / error / empty are all non-fatal (the readout above still
 * stands).
 *
 * @param {AffectedSlotsDrillInProps} props - The calculation, which half to show, and the close handler.
 * @return {JSX.Element} The drill-in surface.
 */
export function AffectedSlotsDrillIn({
  calculationId,
  filter,
  onClose,
}: AffectedSlotsDrillInProps) {
  const { data, isPending, isError, refetch } = useAffectedSlots(calculationId);

  // Each "View" shows its own half of the list: the unscheduled rows, or the
  // unvalued rows (the server labels each row with one reason and the flag).
  const wantUnscheduled = filter === 'unscheduled';
  const rows = (data?.slots ?? []).filter(
    (row) => row.unscheduled === wantUnscheduled
  );
  const copy = COPY[filter];
  const subtitle =
    data !== undefined
      ? `${format0(rows.length)} ${copy.noun(rows.length)}`
      : undefined;

  return (
    <div className={styles.drillIn}>
      <DrillInHeader title={copy.title} subtitle={subtitle} onBack={onClose} />

      {isPending && (
        <DataTableSkeleton
          columnCount={4}
          rowCount={5}
          showHeader={false}
          showToolbar={false}
        />
      )}

      {isError && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the affected properties"
          subtitle="The coverage detail is unavailable right now."
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}

      {data !== undefined &&
        (rows.length > 0 ? (
          <AffectedSlotsTable rows={rows} />
        ) : (
          <p className={styles.empty}>{copy.empty}</p>
        ))}
    </div>
  );
}
